import { useState } from 'react';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
    Field,
    FieldType,
    GeneratorConfigV1,
    Step,
    VisibilityRule,
} from '../../types/generator-config';
import { createField, randomId } from '../../lib/fieldFactory';
import { FieldEditor } from './FieldEditor';
import { Icon } from '../Icon';
import { AddFieldMenu } from './AddFieldMenu';
import { CollapsibleBlock } from './CollapsibleBlock';
import { ConditionBuilder } from './ConditionBuilder';

interface StepEditorProps {
    step: Step;
    index: number;
    config: GeneratorConfigV1;
    onChange: (next: Step) => void;
    onRemove: () => void;
    onDuplicate: () => void;
}

export function StepEditor({ step, index, config, onChange, onRemove, onDuplicate }: StepEditorProps) {
    const [expanded, setExpanded] = useState(true);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
    );

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: step.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const setField = (nextField: Field) => {
        onChange({
            ...step,
            fields: step.fields.map((f) => (f.id === nextField.id ? nextField : f)),
        });
    };
    const removeField = (fieldId: string) => {
        onChange({ ...step, fields: step.fields.filter((f) => f.id !== fieldId) });
    };
    const duplicateField = (fieldId: string) => {
        const src = step.fields.find((f) => f.id === fieldId);
        if (!src) return;
        const copy: Field = { ...src, id: randomId(), label: `${src.label} (copie)` } as Field;
        const idx = step.fields.findIndex((f) => f.id === fieldId);
        const next = [...step.fields];
        next.splice(idx + 1, 0, copy);
        onChange({ ...step, fields: next });
    };
    const addField = (type: FieldType) => {
        onChange({ ...step, fields: [...step.fields, createField(type)] });
        setExpanded(true);
    };

    const handleFieldDragEnd = (e: DragEndEvent) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIdx = step.fields.findIndex((f) => f.id === active.id);
        const newIdx = step.fields.findIndex((f) => f.id === over.id);
        onChange({ ...step, fields: arrayMove(step.fields, oldIdx, newIdx) });
    };

    return (
        <li ref={setNodeRef} style={style} className={`step-card${expanded ? ' is-expanded' : ''}`}>
            <header className="step-card-head">
                <button
                    type="button"
                    className="drag-handle drag-handle-step"
                    aria-label="Réordonner l'étape"
                    {...attributes}
                    {...listeners}
                >
                    <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden>
                        <circle cx="2" cy="2" r="1.2" /><circle cx="8" cy="2" r="1.2" />
                        <circle cx="2" cy="7" r="1.2" /><circle cx="8" cy="7" r="1.2" />
                        <circle cx="2" cy="12" r="1.2" /><circle cx="8" cy="12" r="1.2" />
                    </svg>
                </button>

                <span className="step-card-idx" aria-hidden>{index + 1}</span>

                <input
                    type="text"
                    className="step-card-title"
                    value={step.title}
                    onChange={(e) => onChange({ ...step, title: e.target.value })}
                    placeholder="Titre de l'étape"
                />

                <button
                    type="button"
                    className="step-card-toggle"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Replier' : 'Déplier'}
                >
                    <svg
                        width="16" height="16" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        className="step-card-chevron"
                        aria-hidden
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                <div className="step-card-actions">
                    <button
                        type="button"
                        className="icon-btn"
                        onClick={onDuplicate}
                        title="Dupliquer l'étape"
                        aria-label="Dupliquer"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        onClick={onRemove}
                        title="Supprimer l'étape"
                        aria-label="Supprimer"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        </svg>
                    </button>
                </div>
            </header>

            {expanded && (
                <div className="step-card-body">
                    <label className="field">
                        <span className="field-label">Description (optionnelle)</span>
                        <input
                            type="text"
                            value={step.description ?? ''}
                            onChange={(e) =>
                                onChange({
                                    ...step,
                                    description: e.target.value || undefined,
                                })
                            }
                            placeholder="Petit texte affiché sous le titre de l'étape"
                        />
                    </label>

                    {step.fields.length > 0 ? (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleFieldDragEnd}
                        >
                            <SortableContext
                                items={step.fields.map((f) => f.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <ul className="fields-list">
                                    {step.fields.map((f) => (
                                        <FieldEditor
                                            key={f.id}
                                            field={f}
                                            config={config}
                                            onChange={setField}
                                            onRemove={() => removeField(f.id)}
                                            onDuplicate={() => duplicateField(f.id)}
                                        />
                                    ))}
                                </ul>
                            </SortableContext>
                        </DndContext>
                    ) : (
                        <div className="fields-empty">
                            <p>Aucun champ dans cette étape pour l'instant.</p>
                        </div>
                    )}

                    <AddFieldMenu onAdd={addField} />

                    <CollapsibleBlock
                        title="Afficher cette étape conditionnellement"
                        icon={<Icon name="branch" size={15} />}
                        hasValue={!!step.visibility}
                    >
                        <label className="field field-checkbox">
                            <input
                                type="checkbox"
                                checked={!!step.visibility}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        onChange({ ...step, visibility: { fieldId: '' } });
                                    } else {
                                        const { visibility: _v, ...rest } = step as Step & {
                                            visibility?: VisibilityRule;
                                        };
                                        onChange(rest as Step);
                                    }
                                }}
                            />
                            <span>N'afficher cette étape que si une condition est remplie</span>
                        </label>
                        {step.visibility && (
                            <ConditionBuilder
                                config={config}
                                rule={step.visibility}
                                onChange={(rule) => {
                                    if (rule === undefined) {
                                        const { visibility: _v, ...rest } = step as Step & {
                                            visibility?: VisibilityRule;
                                        };
                                        onChange(rest as Step);
                                    } else {
                                        onChange({ ...step, visibility: rule });
                                    }
                                }}
                            />
                        )}
                    </CollapsibleBlock>
                </div>
            )}
        </li>
    );
}
